import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { PHONE_HREF } from "../lib/site";

const MaskedLine = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.1]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative isolate min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="/images/hero.png"
          alt="Tow truck with amber beacon lights on a snowy Minneapolis street at night"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#050505] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-5 md:px-8 pt-32 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          24/7 Mobile Roadside Assistance — Minneapolis, MN
        </motion.p>

        <h1 className="font-display font-bold uppercase tracking-normal leading-[0.95] text-5xl sm:text-7xl lg:text-8xl" data-testid="hero-heading">
          <MaskedLine delay={0.1}>Stranded in</MaskedLine>
          <MaskedLine delay={0.25}>Minneapolis?</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-primary">We come to you.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-8 max-w-xl text-neutral-300 text-base md:text-lg"
          data-testid="hero-subheading"
        >
          Local technician. Upfront price. No surprise night/weekend fees. One call and the van is rolling — jumps, tires, lockouts, fuel, and batteries.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a data-testid="hero-get-help-btn" href={PHONE_HREF} className="btn-primary">
            <Phone size={16} /> Get Help Now
          </a>
          <a data-testid="hero-pricing-btn" href="/pricing" className="btn-ghost">
            See Upfront Pricing <ArrowDown size={16} />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="border-t border-white/10"
        data-testid="hero-stats"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {[
            ["24/7 / 365", "Dispatch — we answer at 3 AM"],
            ["$0", "After-hours or weekend fees"],
            ["Flat", "Upfront pricing before we roll"],
          ].map(([big, small]) => (
            <div key={small} className="py-5 sm:pr-8 sm:pl-8 first:pl-0">
              <span className="font-mono text-primary text-lg font-semibold">{big}</span>
              <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 mt-1">{small}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
